/**
 * CategorySelector Component
 * Dropdown selector for expense categories
 */

import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../utils/cn';

export interface Category {
  id: string;
  name: string;
  description?: string;
  icon?: React.ReactNode;
  color?: string;
  parentId?: string;
}

export interface CategorySelectorProps {
  categories: Category[];
  value?: string;
  onChange?: (categoryId: string | null) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  allowClear?: boolean;
  className?: string;
}

const defaultCategories: Category[] = [
  {
    id: 'travel',
    name: 'Travel',
    description: 'Transportation, hotels, flights',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>,
    color: 'bg-blue-100 text-blue-800',
  },
  {
    id: 'meals',
    name: 'Meals & Entertainment',
    description: 'Business meals, client entertainment',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" /></svg>,
    color: 'bg-green-100 text-green-800',
  },
  {
    id: 'office',
    name: 'Office Supplies',
    description: 'Stationery, equipment, software',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>,
    color: 'bg-purple-100 text-purple-800',
  },
  {
    id: 'marketing',
    name: 'Marketing',
    description: 'Advertising, events, promotional materials',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" /></svg>,
    color: 'bg-pink-100 text-pink-800',
  },
  {
    id: 'training',
    name: 'Training & Development',
    description: 'Courses, conferences, certifications',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>,
    color: 'bg-indigo-100 text-indigo-800',
  },
  {
    id: 'other',
    name: 'Other',
    description: 'Miscellaneous business expenses',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" /></svg>,
    color: 'bg-gray-100 text-gray-800',
  },
];

export function CategorySelector({
  categories = defaultCategories,
  value,
  onChange,
  placeholder = 'Select a category',
  label,
  error,
  disabled = false,
  allowClear = true,
  className,
}: CategorySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedCategory = categories.find(cat => cat.id === value);

  // Filter categories based on search term
  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (categoryId: string) => {
    onChange?.(categoryId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredCategories.length > 0) {
        handleSelect(filteredCategories[0].id);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  return (
    <div className={cn('relative w-full', className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      <div
        ref={dropdownRef}
        className={cn(
          'relative border rounded-md bg-white',
          error ? 'border-error-500' : 'border-gray-300',
          disabled ? 'bg-gray-50 cursor-not-allowed' : 'cursor-pointer',
          'focus-within:ring-2 focus-within:ring-primary-600 focus-within:border-primary-600'
        )}
      >
        <div
          className="flex items-center justify-between p-3"
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {selectedCategory ? (
              <>
                {selectedCategory.icon && (
                  <span className="flex-shrink-0 text-gray-400">
                    {selectedCategory.icon}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {selectedCategory.name}
                  </div>
                  {selectedCategory.description && (
                    <div className="text-xs text-gray-500 truncate">
                      {selectedCategory.description}
                    </div>
                  )}
                </div>
                {selectedCategory.color && (
                  <span className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                    selectedCategory.color
                  )}>
                    {selectedCategory.name}
                  </span>
                )}
              </>
            ) : (
              <span className="text-gray-400 text-sm">{placeholder}</span>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {allowClear && selectedCategory && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <svg
              className={cn(
                'w-4 h-4 text-gray-400 transition-transform',
                isOpen && 'transform rotate-180'
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && !disabled && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
            {/* Search input */}
            <div className="p-2 border-b border-gray-200">
              <input
                ref={inputRef}
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-primary-600"
                autoFocus
              />
            </div>

            {/* Category list */}
            <div className="max-h-60 overflow-y-auto">
              {filteredCategories.length > 0 ? (
                filteredCategories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => handleSelect(category.id)}
                    className={cn(
                      'w-full flex items-center space-x-3 p-3 text-left hover:bg-gray-50 transition-colors',
                      category.id === value && 'bg-primary-50 text-primary-700'
                    )}
                  >
                    {category.icon && (
                      <span className="flex-shrink-0 text-gray-400">
                        {category.icon}
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {category.name}
                      </div>
                      {category.description && (
                        <div className="text-xs text-gray-500">
                          {category.description}
                        </div>
                      )}
                    </div>
                    {category.color && (
                      <span className={cn(
                        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                        category.color
                      )}>
                        {category.name}
                      </span>
                    )}
                  </button>
                ))
              ) : (
                <div className="p-3 text-sm text-gray-500 text-center">
                  No categories found
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-error-600 flex items-center">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
}
